import React from "react";
import type { ChartComponentProps } from "./types";
import { ChartContainer } from "./ChartContainer";
import { useChartAnimation } from "./useChartAnimation";
import {
  FONT,
  formatChartValue,
  estimateTextWidth,
  useAvailableWidth,
  computeYAxis,
} from "./chartUtils";
import { E } from "./chartColors";
import { ChartTitle } from "./ChartTitle";

const BAR_RADIUS = 8;
const BAR_WIDTH = 80;
const MIN_CELL_GAP = 8;
const FIGMA_W = 786;
const FIGMA_PAD_LEFT = 80;

export const VerticalBarChart: React.FC<ChartComponentProps> = ({
  chartData,
  ctx,
}) => {
  const { S } = ctx;
  const anim = useChartAnimation(ctx, chartData);
  const bars = chartData.bars || [];
  const [measureRef, availPx] = useAvailableWidth();

  if (bars.length === 0) {
    return null;
  }

  const values = bars.map((b) => b.value);
  const {
    yMin,
    yMax,
    ticks: yTickValues,
    hasNeg,
  } = computeYAxis(values, { includeZero: true });

  const padTop = 12;
  const plotH = 340;

  const formatVal = (v: number) => formatChartValue(v);

  const maxLabelWidth = Math.max(
    ...yTickValues.map((v) => estimateTextWidth(formatVal(v), 20)),
  );
  const padLeft = Math.max(FIGMA_PAD_LEFT, Math.ceil(maxLabelWidth) + 24);

  const W_from_container = availPx > 0 ? Math.round(availPx / S) : FIGMA_W;
  const contentMinW = padLeft + bars.length * (BAR_WIDTH + MIN_CELL_GAP);
  const W = Math.max(FIGMA_W, contentMinW, W_from_container);
  const plotW = W - padLeft;
  const H = padTop + plotH + 72;

  const cellW = plotW / bars.length;

  const getY = (v: number) => padTop + (1 - (v - yMin) / (yMax - yMin)) * plotH;
  const zeroY = getY(0);

  const getBarX = (i: number) => padLeft + cellW * i + (cellW - BAR_WIDTH) / 2;

  return (
    <ChartContainer ctx={ctx} chartData={chartData} width={W}>
      <div ref={measureRef} style={{ width: "100%" }}>
        {chartData.label && (
          <ChartTitle label={chartData.label} unit={chartData.unit} S={S} />
        )}

        <svg viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          {yTickValues.map((val, i) => {
            const y = getY(val);
            return (
              <g key={i}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={W}
                  y2={y}
                  stroke={E.chartGrid}
                  strokeWidth={1}
                />
                <text
                  x={padLeft - 8}
                  y={y + 7}
                  textAnchor="end"
                  fill={E.muted}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={400}
                >
                  {formatVal(val)}
                </text>
              </g>
            );
          })}

          {hasNeg && (
            <line
              x1={padLeft}
              y1={zeroY}
              x2={W}
              y2={zeroY}
              stroke={E.muted}
              strokeWidth={1.5}
              strokeOpacity={0.6}
            />
          )}

          {bars.map((bar, i) => {
            const stagger = anim.stagger(i, 0.15);
            const valY = getY(bar.value);
            const rawH = Math.abs(valY - zeroY);
            const bH = rawH * stagger.progress;
            const isNeg = bar.value < 0;
            const bY = isNeg ? zeroY : zeroY - bH;
            const x = getBarX(i);
            const labelY = isNeg ? bY + bH + 24 : bY - 8;
            return (
              <g key={i} opacity={stagger.opacity}>
                <text
                  x={x + BAR_WIDTH / 2}
                  y={labelY}
                  textAnchor="middle"
                  fill={E.t3}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={500}
                >
                  {formatVal(bar.value)}
                </text>
                <rect
                  x={x}
                  y={bY}
                  width={BAR_WIDTH}
                  height={Math.max(bH, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.bgPage}
                />
                <rect
                  x={x}
                  y={bY}
                  width={BAR_WIDTH}
                  height={Math.max(bH, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={isNeg ? E.orange : E.cyan}
                  fillOpacity={0.8}
                />
              </g>
            );
          })}

          {bars.map((bar, i) => {
            const cx = padLeft + cellW * i + cellW / 2;
            const labelW = Math.min(cellW - 4, 180);
            return (
              <foreignObject
                key={i}
                x={cx - labelW / 2}
                y={padTop + plotH + 8}
                width={labelW}
                height={56}
              >
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    fontFamily: FONT,
                    fontSize: 20,
                    fontWeight: 400,
                    color: E.muted,
                    lineHeight: "24px",
                  }}
                >
                  {bar.label}
                </div>
              </foreignObject>
            );
          })}
        </svg>
      </div>
    </ChartContainer>
  );
};
